import React from 'react';

// Función para formatear fechas correctamente en zona horaria de México
const formatDateForMexico = (dateString) => {
  if (!dateString) return '';
  
  try {
    // Si la fecha viene como string ISO (ej: "2025-06-12T00:00:00.000Z")
    if (dateString.includes('T')) {
      const [datePart] = dateString.split('T');
      const [year, month, day] = datePart.split('-');
      
      // Crear fecha usando UTC para evitar problemas de zona horaria
      const date = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0));
      
      return date.toLocaleDateString('es-MX', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } 
    // Si la fecha viene como string de la BD (ej: "2025-06-12" o "2025-06-12 00:00:00")
    else {
      const dateOnly = dateString.split(' ')[0]; // Tomar solo la parte de la fecha
      const [year, month, day] = dateOnly.split('-');
      
      // Usar Date.UTC para crear la fecha en UTC y evitar problemas de zona horaria
      const date = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0));
      
      return date.toLocaleDateString('es-MX', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    }
  } catch (error) {
    console.error('Error al formatear fecha:', error, dateString);
    return dateString; // Devolver la fecha original en caso de error
  }
};

// Función para formatear fecha corta
const formatShortDateForMexico = (dateString) => {
  if (!dateString) return '';
  
  try {
    // Si la fecha viene como string ISO (ej: "2025-06-12T00:00:00.000Z")
    if (dateString.includes('T')) {
      const [datePart] = dateString.split('T');
      const [year, month, day] = datePart.split('-');
      
      // Crear fecha usando UTC para evitar problemas de zona horaria
      const date = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0));
      
      return date.toLocaleDateString('es-MX');
    }
    // Si la fecha viene como string de la BD (ej: "2025-06-12" o "2025-06-12 00:00:00")
    else {
      const dateOnly = dateString.split(' ')[0];
      const [year, month, day] = dateOnly.split('-');
      
      // Usar Date.UTC para crear la fecha en UTC y evitar problemas de zona horaria
      const date = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0));
      
      return date.toLocaleDateString('es-MX');
    }
  } catch (error) {
    console.error('Error al formatear fecha corta:', error, dateString);
    return dateString; // Devolver la fecha original en caso de error
  }
};

const ClientTimeline = ({ meetings, onViewSummary }) => {
  return (
    <div className="mt-6">
      <h3 className="text-lg font-semibold text-[#0061A8] mb-4">Historial de Juntas</h3>
      <div className="space-y-6">
        {meetings.map((meeting, index) => {
          const contacts = meeting.contactsGenerated;
          const patients = meeting.patients;
          const conversion = contacts > 0 ? ((patients / contacts) * 100).toFixed(1) + '%' : '0%';

          // Calcular el máximo para escalar las barras de rendimiento por junta
          const maxContactsPatients = Math.max(contacts, patients);
          const contactsWidth = maxContactsPatients > 0 ? (contacts / maxContactsPatients) * 100 : 0;
          const patientsWidth = maxContactsPatients > 0 ? (patients / maxContactsPatients) * 100 : 0;
          const conversionWidth = parseFloat(conversion) > 0 ? parseFloat(conversion) : 0; // Max 100%

          const totalObjections = (meeting.objections?.price || 0) + (meeting.objections?.location || 0) + (meeting.objections?.noResponse || 0) + (meeting.objections?.schedule || 0);
          const totalServicesValue = (meeting.servicesSold || []).reduce((sum, service) => sum + (service.quantity * parseFloat(service.value || 0)), 0);

          return (
            <div key={meeting.id} className="relative pl-6 border-l-2 border-[#D1D3D4]">
              <div className="absolute w-4 h-4 bg-[#D1D3D4] rounded-full -left-2 top-1"></div>
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <div className="flex justify-between items-start">
                  <h4 className="font-medium text-[#0061A8]">
                    Junta #{meetings.length - index} - {formatDateForMexico(meeting.date)}
                  </h4>
                  <span className="text-sm text-[#63666A]">
                    {formatShortDateForMexico(meeting.date)}
                  </span>
                </div>
                
                <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-[#63666A]">Contactos:</span> {contacts}
                  </div>
                  <div>
                    <span className="text-[#63666A]">Pacientes:</span> {patients}
                  </div>
                </div>
                
                {meeting.nextMeetingDate && (
                  <div className="mt-2 text-sm">
                    <span className="text-[#63666A]">Próxima junta:</span> {formatShortDateForMexico(meeting.nextMeetingDate)}
                    {meeting.nextMeetingTime && ` a las ${meeting.nextMeetingTime} hrs`}
                  </div>
                )}
                
                {meeting.clientMeetingEmail && (
                  <div className="mt-2 text-sm">
                    <span className="text-[#63666A]">Email agendamiento:</span> {meeting.clientMeetingEmail}
                  </div>
                )}

                {meeting.meetingType && (
                  <div className="mt-2 text-sm">
                    <span className="text-[#63666A]">Tipo de Junta:</span> {meeting.meetingType}
                  </div>
                )}

                <div className="mt-3">
                  <h5 className="text-sm font-medium text-[#63666A]">Resumen:</h5>
                  <p className="text-sm text-[#63666A] whitespace-pre-line">{meeting.observations}</p>
                </div>
                
                <div className="mt-2">
                  <h5 className="text-sm font-medium text-[#63666A]">Acciones:</h5>
                  <p className="text-sm text-[#63666A] whitespace-pre-line">{meeting.agreements}</p>
                </div>

                {/* Análisis de Objeciones */}
                <div className="mt-3">
                  <h5 className="text-sm font-medium text-[#0061A8]">Análisis de Objeciones</h5>
                  <div className="mt-2 bg-gray-50 p-2 rounded-md">
                    {totalObjections > 0 ? (
                      <div>
                        <div className="flex justify-between text-xs text-[#63666A] mb-1">
                          <span>Total de objeciones:</span>
                          <span className="font-semibold">{totalObjections}</span>
                        </div>
                        <div className="w-full bg-[#D1D3D4] rounded-full h-2 mb-3">
                          <div className="bg-red-500 h-2 rounded-full" style={{ width: `${Math.min(100, totalObjections * 5)}%` }}></div>
                        </div>
                        
                        {/* Distribución de objeciones */}
                        {meeting.objections.price > 0 && (
                          <div className="mb-2">
                            <div className="flex justify-between text-xs text-[#63666A]">
                              <span>Precio:</span>
                              <span>{meeting.objections.price}</span>
                            </div>
                            <div className="w-full bg-[#D1D3D4] rounded-full h-1.5">
                              <div className="bg-red-400 h-1.5 rounded-full" style={{ width: `${(meeting.objections.price / totalObjections) * 100}%` }}></div>
                            </div>
                          </div>
                        )}
                        
                        {meeting.objections.location > 0 && (
                          <div className="mb-2">
                            <div className="flex justify-between text-xs text-[#63666A]">
                              <span>Ubicación:</span>
                              <span>{meeting.objections.location}</span>
                            </div>
                            <div className="w-full bg-[#D1D3D4] rounded-full h-1.5">
                              <div className="bg-yellow-400 h-1.5 rounded-full" style={{ width: `${(meeting.objections.location / totalObjections) * 100}%` }}></div>
                            </div>
                          </div>
                        )}
                        
                        {meeting.objections.noResponse > 0 && (
                          <div className="mb-2">
                            <div className="flex justify-between text-xs text-[#63666A]">
                              <span>No contestan:</span>
                              <span>{meeting.objections.noResponse}</span>
                            </div>
                            <div className="w-full bg-[#D1D3D4] rounded-full h-1.5">
                              <div className="bg-blue-400 h-1.5 rounded-full" style={{ width: `${(meeting.objections.noResponse / totalObjections) * 100}%` }}></div>
                            </div>
                          </div>
                        )}
                        
                        {meeting.objections.schedule > 0 && (
                          <div className="mb-2">
                            <div className="flex justify-between text-xs text-[#63666A]">
                              <span>Horarios:</span>
                              <span>{meeting.objections.schedule}</span>
                            </div>
                            <div className="w-full bg-[#D1D3D4] rounded-full h-1.5">
                              <div className="bg-green-400 h-1.5 rounded-full" style={{ width: `${(meeting.objections.schedule / totalObjections) * 100}%` }}></div>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-[#63666A]">No se registraron objeciones en esta junta.</p>
                    )}
                  </div>
                </div>
                
                {/* Objeciones Registradas */}
                {totalObjections > 0 && (
                  <div className="mt-2">
                    <h5 className="text-sm font-medium text-[#0061A8]">Objeciones Registradas:</h5>
                    <ul className="list-disc list-inside text-sm text-[#63666A]">
                      {meeting.objections.price > 0 && <li>Precio: {meeting.objections.price} personas</li>}
                      {meeting.objections.location > 0 && <li>Ubicación: {meeting.objections.location} personas</li>}
                      {meeting.objections.noResponse > 0 && <li>No contestan 1er mensaje: {meeting.objections.noResponse} personas</li>}
                      {meeting.objections.schedule > 0 && <li>Horarios: {meeting.objections.schedule} personas</li>}
                      {meeting.objections.other && <li>Otras: {meeting.objections.other}</li>}
                    </ul>
                  </div>
                )}

                {/* Servicios Vendidos con análisis gráfico */}
                {totalServicesValue > 0 && (
                  <div className="mt-3">
                    <h5 className="text-sm font-medium text-[#0061A8]">Servicios Vendidos</h5>
                    <div className="mt-2 bg-gray-50 p-2 rounded-md">
                      <div className="flex justify-between text-xs text-[#63666A] mb-1">
                        <span>Valor total:</span>
                        <span className="font-semibold">${totalServicesValue.toFixed(2)}</span>
                      </div>
                      <div className="w-full bg-[#D1D3D4] rounded-full h-2 mb-3">
                        <div className="bg-green-500 h-2 rounded-full" style={{ width: `${Math.min(100, totalServicesValue / 100)}%` }}></div>
                      </div>
                      
                      {/* Distribución de servicios */}
                      {(meeting.servicesSold || []).map((service, sIndex) => service.name && (
                        <div key={sIndex} className="mb-2">
                          <div className="flex justify-between text-xs text-[#63666A]">
                            <span>{service.name}:</span>
                            <span>{service.quantity} unidades (${(service.quantity * parseFloat(service.value)).toFixed(2)})</span>
                          </div>
                          <div className="w-full bg-[#D1D3D4] rounded-full h-1.5">
                            <div className="bg-teal-400 h-1.5 rounded-full" style={{ width: `${(service.quantity * parseFloat(service.value) / totalServicesValue) * 100}%` }}></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Servicios Más Vendidos (lista detallada) */}
                {totalServicesValue > 0 && (
                  <div className="mt-2">
                    <h5 className="text-sm font-medium text-[#0061A8]">Servicios Más Vendidos:</h5>
                    <ul className="list-disc list-inside text-sm text-[#63666A]">
                      {(meeting.servicesSold || [])
                        .sort((a, b) => (b.quantity * parseFloat(b.value)) - (a.quantity * parseFloat(a.value)))
                        .map((service, sIndex) => service.name && (
                          <li key={sIndex} className={sIndex === 0 ? "font-semibold" : ""}>
                            {service.name}: {service.quantity} unidades, Valor: ${parseFloat(service.value).toFixed(2)} c/u 
                            (Total: ${(service.quantity * parseFloat(service.value)).toFixed(2)})
                            {sIndex === 0 && <span className="ml-1 text-green-600">★</span>}
                          </li>
                        ))}
                    </ul>
                    <p className="text-sm font-bold text-[#0061A8] mt-1">Valor total: ${totalServicesValue.toFixed(2)}</p>
                  </div>
                )}

                {/* Barras de rendimiento por junta */}
                <div className="mt-4 space-y-2">
                  <h6 className="text-sm font-semibold text-[#0061A8]">Rendimiento de esta Junta</h6>
                  <div>
                    <div className="flex justify-between text-xs text-[#63666A]">
                      <span>Contactos: {contacts}</span>
                      <span>{contacts}</span>
                    </div>
                    <div className="w-full bg-[#D1D3D4] rounded-full h-2">
                      <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${contactsWidth}%` }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs text-[#63666A]">
                      <span>Pacientes: {patients}</span>
                      <span>{patients}</span>
                    </div>
                    <div className="w-full bg-[#D1D3D4] rounded-full h-2">
                      <div className="bg-green-500 h-2 rounded-full" style={{ width: `${patientsWidth}%` }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs text-[#63666A]">
                      <span>Tasa de Conversión: {conversion}</span>
                      <span>{conversion}</span>
                    </div>
                    <div className="w-full bg-[#D1D3D4] rounded-full h-2">
                      <div className="bg-purple-500 h-2 rounded-full" style={{ width: `${conversionWidth}%` }}></div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex justify-end space-x-2">
                  {meeting.googleCalendarLink && (
                    <a
                      href={meeting.googleCalendarLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm bg-[#A8D08D] text-[#FFFFFF] py-1 px-3 rounded-md hover:bg-opacity-90 transition-colors flex items-center"
                    >
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17 12h-2v2h2v-2zm-4 0h-2v2h2v-2zm-4 0H7v2h2v-2zm10-9h-1V2h-2v1H8V2H6v1H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11z"/>
                      </svg>
                      Agendar
                    </a>
                  )}
                  <button
                    onClick={() => onViewSummary(meeting)}
                    className="text-sm bg-[#0061A8] text-[#FFFFFF] py-1 px-3 rounded-md hover:bg-opacity-90 transition-colors"
                  >
                    Ver Minuta
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ClientTimeline;
